import { useState, type CSSProperties } from 'react';
import { Link } from 'react-router-dom';
import { Badge, Button, Input, Select } from '../atoms';
import { Modal } from '../organisms';
import { useOrgTimezone, zonedInputToUtcIso, utcIsoToZonedInput, formatAbsInTz, formatDate } from '../lib/orgTimezone';

/**
 * CalendarShared — the pieces the org Calendar (CalendarPage) and the per-business
 * Calendar tab (ClientDetail) both render, so the two surfaces stay identical:
 *   • the CalendarEvent shape (incl. client_name, the one joined linkage NAME the
 *     backend returns);
 *   • the event-type metadata (label + colour);
 *   • the create/edit EventModal (with an optional locked business for the
 *     per-business tab);
 *   • the EventLinkage renderer that shows the linked business on every event row
 *     so events read as connected, not "unlinked".
 *
 * A calendar event links to exactly ONE record: the business (client_id). That is
 * the only FK the calendar_events table carries, so there is one picker, one chip,
 * and one click-through target — no linkage chain to cascade through.
 */

/**
 * The dated things in the supplier journey. Must match EVENT_TYPES in
 * src/services/calendar-events.js AND the schema CHECK on calendar_events —
 * a value present here and absent there is a 400 on save.
 */
export type EventType =
  | 'session'
  | 'cohort_milestone'
  | 'assessment_due'
  | 'referral_followup'
  | 'info_request_due'
  | 'reminder';

/**
 * Is this date a placeholder or firm? Every pre-existing event reads 'confirmed'
 * (the column default), so nothing silently becomes tentative.
 */
export type DateConfidence = 'tentative' | 'confirmed';

export interface CalendarEvent {
  id: string;
  organization_id: string;
  client_id: string | null;
  event_type: EventType;
  title: string | null;
  event_date: string; // YYYY-MM-DD
  event_time: string | null;
  location: string | null;
  state: string | null;
  assigned_user_id: string | null;
  assigned_contact_id: string | null;
  status: string | null;
  remind_at: string | null;
  source: string | null;
  notes: string | null;
  /** 'tentative' | 'confirmed'. Optional on the wire so an older cached row still parses. */
  date_confidence?: DateConfidence | null;
  created_by_user_id: string | null;
  created_at: string;
  updated_at: string;
  /** Joined business name (read-only, from the LEFT JOIN in the service). NULL when unlinked. */
  client_name?: string | null;
}

export interface UserLite {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  role: string;
}
export interface NamedRow { id: string; name: string }

export const TYPE_META: Record<EventType, { label: string; variant: 'neutral' | 'info' | 'warning' | 'success' | 'danger'; accent: string }> = {
  session: { label: 'Session', variant: 'info', accent: 'var(--color-status-blue)' },
  cohort_milestone: { label: 'Cohort milestone', variant: 'success', accent: 'var(--color-status-green)' },
  assessment_due: { label: 'Assessment due', variant: 'warning', accent: 'var(--color-status-amber)' },
  referral_followup: { label: 'Referral follow-up', variant: 'neutral', accent: 'var(--color-ink-3)' },
  info_request_due: { label: 'Information request due', variant: 'danger', accent: 'var(--color-status-red)' },
  reminder: { label: 'Reminder', variant: 'neutral', accent: 'var(--color-muted)' },
};
export const TYPE_ORDER: EventType[] = [
  'session', 'cohort_milestone', 'info_request_due', 'assessment_due', 'referral_followup', 'reminder',
];

/**
 * Safe lookup for a DB-sourced event_type. TYPE_META is typed as total over EventType,
 * but the stored value could be a type added via a dropped CHECK without updating the
 * map (this codebase drops enum CHECKs to make them org-configurable — that is exactly
 * how the re_pp crash arose). An unknown key falls back instead of `undefined.label`
 * white-screening the Calendar, the Dashboard upcoming panel, and the client calendar.
 */
type EventMeta = { label: string; variant: 'neutral' | 'info' | 'warning' | 'success' | 'danger'; accent: string };
const DEFAULT_EVENT_META: EventMeta = { label: 'Event', variant: 'neutral', accent: 'var(--color-ink-3)' };
export function metaFor(t: string | null | undefined): EventMeta {
  return (TYPE_META as Record<string, EventMeta>)[t ?? ''] ?? DEFAULT_EVENT_META;
}

// ── B4: tentative vs confirmed ───────────────────────────────────────────────

/**
 * Is this event's date still a placeholder? Anything but an explicit 'tentative'
 * reads as firm — including a row from before the column existed — so the
 * distinction can only ever make a date LESS authoritative than today, never more.
 */
export function isTentative(e: { date_confidence?: DateConfidence | null }): boolean {
  return e.date_confidence === 'tentative';
}

/**
 * The one visual marker for a placeholder date, used everywhere an event renders
 * (month cell, agenda row, upcoming rail, the event profile) so "tentative" reads
 * the same on every surface. The whole point of B4 is that a consultant planning
 * around a date can see at a glance that it may still move.
 */
export function TentativeBadge({ compact }: { compact?: boolean }) {
  if (compact) {
    return (
      <span
        title="Tentative — this date is not confirmed yet"
        style={{
          fontSize: 'var(--fs-xs)', fontWeight: 'var(--fw-semibold)', letterSpacing: 'var(--ls-wider)',
          color: 'var(--color-status-amber, var(--color-muted))',
        }}
      >TENT</span>
    );
  }
  return <Badge variant="warning">Tentative</Badge>;
}

/**
 * The card/row treatment for a tentative event: a DASHED outline instead of the
 * solid one a firm date gets. Dashed is the convention for "provisional" and it
 * survives the badge being clipped in a dense month cell.
 */
export function tentativeRowStyle(e: { date_confidence?: DateConfidence | null }): CSSProperties {
  return isTentative(e) ? { borderStyle: 'dashed', borderWidth: 1 } : {};
}

/** Today as YYYY-MM-DD (local), the default date for a fresh event. */
export function todayIso(): string {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

export function userLabel(users: UserLite[], uid: string | null): string {
  if (!uid) return '';
  const u = users.find((x) => x.id === uid);
  if (!u) return uid;
  return [u.first_name, u.last_name].filter(Boolean).join(' ') || u.email;
}

/**
 * sourceLabel — a short human label for an event's `source`, i.e. WHERE the entry
 * came from. Generated entries carry a machine source tag written by
 * services/calendar-sync.js, which projects the dated columns that already live on
 * other records onto the calendar; render that tag as a readable origin so a user
 * can tell "this was pulled from the programme" from "someone typed this in".
 * Hand-authored events (source null) get no chip.
 *
 * The three cases below are exactly the `source` values calendar-sync.js writes.
 * Keep them in step with that module — an unrecognized source falls through to
 * null and simply renders no chip, never `undefined`.
 */
export function sourceLabel(source: string | null | undefined): string | null {
  switch (source) {
    case 'program_session': return 'Programme session';
    case 'cohort': return 'Cohort';
    case 'information_request': return 'Information request';
    default: return null;
  }
}

/**
 * eventTitle — the human-facing title for an event row, falling back to the type
 * label when the event has none.
 *
 * Deliberately a plain trim: the sync producers in services/calendar-sync.js title
 * their entries in prose ("Coaching session", "Spring 2026 starts", "W-9 due") and
 * render the business as a separate linked chip, so there is no machine id slug
 * glued onto the end for this to strip back off.
 */
export function eventTitle(e: CalendarEvent): string {
  const fallback = metaFor(e.event_type).label;
  return e.title?.trim() || fallback;
}

/**
 * A source-record link an event resolves to. calendar_events carries exactly one
 * FK — client_id, the BUSINESS — so there is exactly one shape of link. It stays a
 * list rather than a bare value so the grid cell, the agenda row, and the modal all
 * click through identically, and so an event with no business renders nothing at
 * all rather than a dead chip.
 */
export interface SourceLink { key: 'client'; to: string; label: string }

/**
 * The navigable source record an event links to: its business, routed to
 * /clients/:id. Uses ONLY the FK the event already carries plus `client_name`, the
 * one joined linkage name the backend returns — no extra projection needed.
 *
 * Keyed off client_id (not client_name) so a business whose name failed to join
 * still produces a working link under a generic label, instead of silently
 * disappearing from the "Linked to" row.
 */
export function eventSourceLinks(event: CalendarEvent): SourceLink[] {
  if (!event.client_id) return [];
  return [{ key: 'client', to: `/clients/${event.client_id}`, label: event.client_name || 'Business' }];
}

/**
 * EventLinkage — the connected-record line shown under an event: the business it
 * belongs to, drawn from the joined name the backend returns. The chip is a real
 * Link that CLICKS THROUGH to that business's detail page. Renders nothing when
 * the event has no business (a personal reminder, or an org-level cohort
 * milestone, which spans many businesses and so links to none).
 */
export function EventLinkage({ event, style }: { event: CalendarEvent; style?: React.CSSProperties }) {
  const links = eventSourceLinks(event);
  if (links.length === 0) return null;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--sp-1)', ...style }}>
      {links.map((l) => (
        <Link
          key={l.key}
          to={l.to}
          onClick={(e) => e.stopPropagation()}
          title={`Open business: ${l.label}`}
          style={{ textDecoration: 'none' }}
        >
          <Badge variant="info">{l.label} ↗</Badge>
        </Link>
      ))}
    </div>
  );
}

// ── Create / edit modal ───────────────────────────────────────────────────────
export function EventModal({
  event, defaultDate, users, clients, lockClient, canEdit, canDelete, saving, deleting, onClose, onSubmit, onDelete,
  onSetDateConfidence, settingConfidence,
}: {
  event: CalendarEvent | null;
  defaultDate: string;
  users: UserLite[];
  /** Business options for the one linkage picker. */
  clients: NamedRow[];
  /** When set, the business is fixed (per-business tab): picker becomes read-only + defaults the link. */
  lockClient?: { id: string; name: string };
  canEdit: boolean;
  canDelete: boolean;
  saving: boolean;
  deleting: boolean;
  onClose: () => void;
  onSubmit: (body: Partial<CalendarEvent>) => void;
  onDelete?: () => void;
  /**
   * B4 — move an existing event's date between tentative and confirmed. A governed
   * transition on its own endpoint, not part of the save body, so it is a separate
   * callback. Omitted (per-client tab) → the control is not offered.
   */
  onSetDateConfidence?: (to: DateConfidence) => void;
  settingConfidence?: boolean;
}) {
  // The org timezone governs how remind_at (an absolute UTC instant) is edited:
  // the datetime-local field shows/accepts the ORG-local wall clock, so an 8:00
  // reminder is stored as the correct UTC instant for 8:00 org-local — never the
  // setter's browser zone (calendar-tz-grid fix #4).
  const orgTz = useOrgTimezone();
  const [eventType, setEventType] = useState<EventType>(event?.event_type ?? 'reminder');
  const [title, setTitle] = useState(event?.title ?? '');
  const [eventDate, setEventDate] = useState(event?.event_date ?? defaultDate);
  const [eventTime, setEventTime] = useState(event?.event_time ?? '');
  const [location, setLocation] = useState(event?.location ?? '');
  const [assignedUserId, setAssignedUserId] = useState(event?.assigned_user_id ?? '');
  const [clientId, setClientId] = useState(event?.client_id ?? lockClient?.id ?? '');
  const [remindAt, setRemindAt] = useState(event?.remind_at ? utcIsoToZonedInput(event.remind_at, orgTz) : '');
  const [status, setStatus] = useState(event?.status ?? '');
  const [notes, setNotes] = useState(event?.notes ?? '');
  // B4 — settable only while CREATING. On an existing event the standing moves
  // through its own transition endpoint (see onSetDateConfidence), so the backend
  // drops it from a generic save and offering it in this form would be a lie.
  const [dateConfidence, setDateConfidence] = useState<DateConfidence>(event?.date_confidence ?? 'confirmed');

  const isEdit = !!event;
  const disabled = !canEdit || saving;

  // Opening an existing event lands on a read-only PROFILE first (the reviewer's
  // ask — clicking an event must not jump straight into an editable form); the
  // Edit button flips to the form below. Creating a new event skips straight to
  // the form. The modal remounts per event (keyed in CalendarPage), so this
  // initializes fresh on every open.
  const [mode, setMode] = useState<'view' | 'edit'>(event ? 'view' : 'edit');

  const submit = () => {
    if (!eventDate) return;
    const body: Partial<CalendarEvent> = {
      event_type: eventType,
      title: title || null,
      event_date: eventDate,
      event_time: eventTime || null,
      location: location || null,
      assigned_user_id: assignedUserId || null,
      // The one linkage a calendar event carries. A locked business (the
      // per-business tab) always wins over the picker's value.
      client_id: (lockClient?.id ?? clientId) || null,
      remind_at: remindAt ? zonedInputToUtcIso(remindAt, orgTz) : null,
      status: status || null,
      notes: notes || null,
    };
    // Only on create — the backend ignores it on an edit by design.
    if (!isEdit) body.date_confidence = dateConfidence;
    onSubmit(body);
  };

  // ── Read-only event profile (view mode) ─────────────────────────────────────
  if (mode === 'view' && event) {
    const links = eventSourceLinks(event);
    const assignee = userLabel(users, event.assigned_user_id);
    return (
      <Modal
        open
        onClose={onClose}
        title="Event details"
        size="lg"
        footer={
          <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', gap: 'var(--sp-2)' }}>
            <div>
              {canDelete && onDelete && (
                <Button variant="ghost" onClick={onDelete} disabled={deleting} style={{ color: 'var(--color-status-red)' }}>
                  {deleting ? 'Deleting…' : 'Delete'}
                </Button>
              )}
            </div>
            <div style={{ display: 'flex', gap: 'var(--sp-2)' }}>
              {/* B4 — the confirm action, right where someone reads the date. */}
              {canEdit && onSetDateConfidence && (
                <Button
                  variant={isTentative(event) ? 'primary' : 'ghost'}
                  disabled={settingConfidence}
                  onClick={() => onSetDateConfidence(isTentative(event) ? 'confirmed' : 'tentative')}
                >
                  {settingConfidence
                    ? 'Saving…'
                    : isTentative(event) ? 'Confirm date' : 'Mark tentative'}
                </Button>
              )}
              <Button variant="secondary" onClick={onClose}>Close</Button>
              {canEdit && <Button variant="primary" onClick={() => setMode('edit')}>Edit</Button>}
            </div>
          </div>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-4)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)', flexWrap: 'wrap' }}>
            <Badge variant={metaFor(event.event_type).variant}>{metaFor(event.event_type).label}</Badge>
            {isTentative(event) && <TentativeBadge />}
            <span style={{ fontSize: 'var(--fs-lg)', fontWeight: 'var(--fw-semibold)', color: 'var(--color-ink)' }}>{eventTitle(event)}</span>
          </div>
          {links.length > 0 && (
            <div style={{
              display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 'var(--sp-2)',
              padding: 'var(--sp-2) var(--sp-3)', background: 'var(--color-surface-2, rgba(0,0,0,0.03))',
              border: '1px solid var(--color-border)', borderRadius: 'var(--r-md, 8px)',
            }}>
              <span style={{ fontSize: 'var(--fs-xs)', fontWeight: 'var(--fw-semibold)', textTransform: 'uppercase', letterSpacing: 'var(--ls-wider)', color: 'var(--color-ink-3)' }}>Linked to</span>
              {links.map((l) => (
                <Link key={l.key} to={l.to} onClick={onClose} style={{ textDecoration: 'none' }}>
                  <Badge variant="info">{l.label} ↗</Badge>
                </Link>
              ))}
            </div>
          )}
          <ViewRow label="When">
            {formatDate(event.event_date)}{event.event_time ? ` · ${event.event_time}` : ''}
            {isTentative(event) && (
              <span style={{ marginLeft: 'var(--sp-2)', fontSize: 'var(--fs-sm)', color: 'var(--color-ink-3)' }}>
                — tentative, not confirmed yet
              </span>
            )}
          </ViewRow>
          {event.location && <ViewRow label="Location">{event.location}</ViewRow>}
          <ViewRow label="Assigned to">{assignee || 'Unassigned'}</ViewRow>
          {event.remind_at && <ViewRow label="Reminder">{`${formatAbsInTz(event.remind_at, orgTz)} · ${orgTz}`}</ViewRow>}
          {event.status && <ViewRow label="Status">{event.status}</ViewRow>}
          {event.notes && <ViewRow label="Notes"><span style={{ whiteSpace: 'pre-wrap' }}>{event.notes}</span></ViewRow>}
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={isEdit ? 'Edit event' : 'New event'}
      size="lg"
      footer={
        <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', gap: 'var(--sp-2)' }}>
          <div>
            {isEdit && canDelete && onDelete && (
              <Button variant="ghost" onClick={onDelete} disabled={deleting} style={{ color: 'var(--color-status-red)' }}>
                {deleting ? 'Deleting…' : 'Delete'}
              </Button>
            )}
          </div>
          <div style={{ display: 'flex', gap: 'var(--sp-2)' }}>
            <Button variant="secondary" onClick={() => (isEdit ? setMode('view') : onClose())}>Cancel</Button>
            <Button variant="primary" onClick={submit} disabled={disabled || !eventDate}>
              {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Create event'}
            </Button>
          </div>
        </div>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-4)' }}>
        {isEdit && event && eventSourceLinks(event).length > 0 && (
          <div style={{
            display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 'var(--sp-2)',
            padding: 'var(--sp-2) var(--sp-3)', background: 'var(--color-surface-2, rgba(0,0,0,0.03))',
            border: '1px solid var(--color-border)', borderRadius: 'var(--r-md, 8px)',
          }}>
            <span style={{ fontSize: 'var(--fs-xs)', fontWeight: 'var(--fw-semibold)', textTransform: 'uppercase', letterSpacing: 'var(--ls-wider)', color: 'var(--color-ink-3)' }}>
              Linked to
            </span>
            {eventSourceLinks(event).map((l) => (
              <Link key={l.key} to={l.to} onClick={onClose} style={{ textDecoration: 'none' }}>
                <Badge variant="info">{l.label} ↗</Badge>
              </Link>
            ))}
          </div>
        )}
        <Field label="Type">
          <Select
            options={TYPE_ORDER.map((t) => ({ value: t, label: TYPE_META[t].label }))}
            value={eventType}
            onChange={(e) => setEventType(e.target.value as EventType)}
            disabled={disabled}
          />
        </Field>
        <Field label="Title">
          <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Short summary" disabled={disabled} />
        </Field>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--sp-3)' }}>
          <Field label="Date">
            <Input type="date" value={eventDate} onChange={(e) => setEventDate(e.target.value)} disabled={disabled} />
          </Field>
          <Field label="Time">
            <Input type="time" value={eventTime} onChange={(e) => setEventTime(e.target.value)} disabled={disabled} />
          </Field>
        </div>
        {/* A session date is often penciled in before the participant confirms it.
            Offered on CREATE only; afterwards the standing moves through its own
            audited transition (the Confirm date button on the event profile). */}
        {!isEdit ? (
          <Field label="Date standing">
            <Select
              aria-label="Date standing"
              options={[
                { value: 'confirmed', label: 'Confirmed — this date is firm' },
                { value: 'tentative', label: 'Tentative — not confirmed yet' },
              ]}
              value={dateConfidence}
              onChange={(e) => setDateConfidence(e.target.value as DateConfidence)}
              disabled={disabled}
            />
          </Field>
        ) : event && isTentative(event) ? (
          <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--color-ink-3)' }}>
            This date is <strong>tentative</strong>. Use “Confirm date” on the event profile once it is firm.
          </div>
        ) : null}
        <Field label="Location">
          <Input value={location} onChange={(e) => setLocation(e.target.value)} placeholder="e.g. Room 2, or a video link" disabled={disabled} />
        </Field>
        <Field label="Assign to">
          <Select
            options={[{ value: '', label: 'Unassigned' }, ...users.map((u) => ({ value: u.id, label: `${[u.first_name, u.last_name].filter(Boolean).join(' ') || u.email} (${u.role})` }))]}
            value={assignedUserId}
            onChange={(e) => setAssignedUserId(e.target.value)}
            disabled={disabled}
          />
        </Field>
        {/* The ONE linkage a calendar event carries: the business it belongs to.
            Locked (read-only) on the per-business Calendar tab, where the event
            can only ever hang off the business whose page you are on. */}
        <Field label="Business">
          {lockClient ? (
            <Input value={lockClient.name} disabled readOnly aria-label="Business" />
          ) : (
            <Select
              aria-label="Business"
              options={[{ value: '', label: 'No business' }, ...clients.map((c) => ({ value: c.id, label: c.name || c.id }))]}
              value={clientId}
              onChange={(e) => setClientId(e.target.value)}
              disabled={disabled}
            />
          )}
        </Field>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--sp-3)' }}>
          <Field label="Remind at">
            <Input type="datetime-local" value={remindAt} onChange={(e) => setRemindAt(e.target.value)} disabled={disabled} />
            <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-muted)' }}>
              {remindAt
                ? `Fires ${formatAbsInTz(zonedInputToUtcIso(remindAt, orgTz), orgTz)} · ${orgTz}`
                : `Org timezone: ${orgTz}`}
            </span>
          </Field>
          <Field label="Status">
            <Input value={status} onChange={(e) => setStatus(e.target.value)} placeholder="e.g. open, done" disabled={disabled} />
          </Field>
        </div>
        <Field label="Notes">
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            disabled={disabled}
            rows={3}
            style={{
              fontFamily: 'inherit', fontSize: 'var(--fs-md)', padding: '8px 12px', width: '100%', resize: 'vertical',
              border: '1px solid var(--color-border)', borderRadius: 'var(--r-md, 8px)', background: 'var(--color-input-bg, transparent)', color: 'var(--color-ink)',
            }}
          />
        </Field>
      </div>
    </Modal>
  );
}

export function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-1)' }}>
      <span style={{ fontSize: 'var(--fs-xs)', fontWeight: 'var(--fw-semibold)', textTransform: 'uppercase', letterSpacing: 'var(--ls-wider)', color: 'var(--color-ink-3)' }}>{label}</span>
      {children}
    </label>
  );
}

/** A read-only labelled row for the event profile (view mode) — mirrors Field's
 *  label styling but renders a static value instead of an input. */
function ViewRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-1)' }}>
      <span style={{ fontSize: 'var(--fs-xs)', fontWeight: 'var(--fw-semibold)', textTransform: 'uppercase', letterSpacing: 'var(--ls-wider)', color: 'var(--color-ink-3)' }}>{label}</span>
      <span style={{ fontSize: 'var(--fs-md)', color: 'var(--color-ink)' }}>{children}</span>
    </div>
  );
}
